// src/pages/MyPage.js
import React, { useState, useEffect, useRef } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  deleteDoc,
  doc,
  updateDoc,
  setDoc,
  getDoc
} from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebaseConfig';
import { QRCodeCanvas } from 'qrcode.react';
import '../App.css';
import CenterLayout from '../components/CenterLayout';
import { useSwipeable } from 'react-swipeable';

export default function MyPage({ user, onLogout, onUpdateNickname, onUpdateIconIdx }) {
  console.log("MyPage 렌더링, 현재 사용자:", user);
  const navigate = useNavigate();
  const [rooms, setRooms]         = useState([]);
  const [posts, setPosts]         = useState([]);
  const [questions, setQuestions] = useState([]);
  const [qrRoom, setQrRoom]       = useState(null);
  const [showQr, setShowQr]       = useState(false);
  const [isMobile, setIsMobile]   = useState(window.innerWidth <= 768);
  
  // 모바일 스와이프 관련 상태 추가
  const [activeTab, setActiveTab] = useState(0); // 0: 내 방, 1: 내 게시물, 2: 내 질문
  
  // 이모지 아바타 (기본 스마일 아이콘)
  const emojiList = ['😀'];
  const [iconIdx, setIconIdx] = useState(() => {
    try {
      const userData = JSON.parse(localStorage.getItem('userData'));
      return userData?.iconIdx ?? 0;
    } catch {
      return 0;
    }
  });
  const [showIconSelect, setShowIconSelect] = useState(false);
  // 정렬 상태 추가
  const [roomSort, setRoomSort] = useState('latest'); // latest, oldest, subject
  const [postSort, setPostSort] = useState('latest');
  const [questionSort, setQuestionSort] = useState('latest');
  
  // 스와이프 핸들러 설정
  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => {
      if (isMobile && activeTab < 2) {
        setActiveTab(activeTab + 1);
      }
    },
    onSwipedRight: () => {
      if (isMobile && activeTab > 0) {
        setActiveTab(activeTab - 1);
      }
    },
    preventDefaultTouchmoveEvent: true,
    trackMouse: false
  });
  
  // 모바일 화면 크기 감지
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // ── 내가 만든 방 실시간 구독 ──
  useEffect(() => {
    if (!user?.id) return;
    const q = query(
      collection(db, 'rooms'),
      where('ownerId', '==', user.id),
      orderBy('createdAt', 'desc')
    );
    return onSnapshot(q, snap =>
      setRooms(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    );
  }, [user?.id]);

  // ── 내가 쓴 게시물 실시간 구독 ──
  useEffect(() => {
    if (!user?.id) return;
    const postMap = {};
    const updatePosts = () =>
      setPosts(
        Object.values(postMap).sort(
          (a, b) =>
            (b.createdAt?.toMillis?.() || 0) -
            (a.createdAt?.toMillis?.() || 0)
        )
      );

    const qById = query(
      collection(db, 'posts'),
      where('authorId', '==', user.id)
    );
    const unsubById = onSnapshot(qById, snap => {
      snap.docs.forEach(d => {
        postMap[d.id] = { id: d.id, ...d.data() };
      });
      updatePosts();
    });

    const qByName = query(
      collection(db, 'posts'),
      where('authorName', '==', user.nickname)
    );
    const unsubByName = onSnapshot(qByName, snap => {
      snap.docs.forEach(d => {
        postMap[d.id] = { id: d.id, ...d.data() };
      });
      updatePosts();
    });

    return () => {
      unsubById();
      unsubByName();
    };
  }, [user?.id, user?.nickname]);

  // ── 내가 쓴 질문 실시간 구독 ──
  useEffect(() => {
    if (!user?.id) {
      setQuestions([]);
      return;
    }
    let unsubList = [];
    const roomsQ = query(
      collection(db, 'rooms'),
      where('ownerId', '==', user.id)
    );
    const unsubRooms = onSnapshot(roomsQ, snapRooms => {
      setQuestions([]);
      unsubList.forEach(u => u());
      unsubList = [];

      snapRooms.docs.forEach(rd => {
        const rid = rd.id;
        const rname = rd.data().name;
        const qsQ = query(
          collection(db, 'rooms', rid, 'questions'),
          orderBy('createdAt', 'desc')
        );
        const unsubQs = onSnapshot(qsQ, snapQs => {
          setQuestions(prev => {
            const filtered = prev.filter(q => q.roomId !== rid);
            const mine = snapQs.docs
              .filter(d => d.data().authorId === user.id)
              .map(d => ({
                id: d.id,
                roomId: rid,
                roomName: rname,
                text: d.data().text,
                createdAt: d.data().createdAt
              }));
            return [...filtered, ...mine].sort(
              (a, b) => b.createdAt.seconds - a.createdAt.seconds
            );
          });
        });
        unsubList.push(unsubQs);
      });
    });
    return () => {
      unsubRooms();
      unsubList.forEach(u => u());
    };
  }, [user?.id]);

  // ── 닉네임 변경 ──
  const handleChangeNick = async () => {
    try {
      const nn = window.prompt('새 닉네임을 입력하세요', user.nickname);
      if (nn && nn.trim() !== user.nickname) {
        console.log("닉네임 변경 시도:", nn.trim());
        
        // Firestore users 컬렉션에서 사용자 문서 참조
        const userRef = doc(db, 'users', user.id);
        
        // 문서가 존재하는지 확인
        const userDoc = await getDoc(userRef);
        
        if (userDoc.exists()) {
          // 문서가 존재하면 업데이트
          await updateDoc(userRef, { nickname: nn.trim() });
          console.log("Firestore 문서 업데이트 완료");
        } else {
          // 문서가 없으면 새로 생성
          await setDoc(userRef, { 
            id: user.id,
            nickname: nn.trim(),
            isAnonymous: user.isAnonymous || false
          });
          console.log("Firestore 문서 생성 완료");
        }
        
        // 부모 컴포넌트의 상태 업데이트
        onUpdateNickname(nn.trim());
        console.log("닉네임 업데이트 완료:", nn.trim());
        
        // 로컬 UI 강제 업데이트
        user.nickname = nn.trim();
        alert("닉네임이 변경되었습니다: " + nn.trim());
      }
    } catch (error) {
      console.error("닉네임 변경 오류:", error);
      alert("닉네임 변경 중 오류가 발생했습니다: " + error.message);
    }
  };

  // ── 삭제 핸들러 ──
  const handleDeleteRoom = async id => {
    if (window.confirm('정말 이 방을 삭제하시겠습니까?')) {
      await deleteDoc(doc(db, 'rooms', id));
    }
  };
  const handleDeletePost = async id => {
    if (window.confirm('정말 이 게시물을 삭제하시겠습니까?')) {
      await deleteDoc(doc(db, 'posts', id));
    }
  };
  const handleDeleteQuestion = async (roomId, qId) => {
    if (window.confirm('정말 이 질문을 삭제하시겠습니까?')) {
      await deleteDoc(doc(db, 'rooms', roomId, 'questions', qId));
    }
  };

  // ── QR 모달 열기/닫기 ──
  const openQrModal = room => {
    setQrRoom(room);
    setShowQr(true);
  };
  const closeQrModal = () => setShowQr(false);

  // ── QR 코드 링크 (검색 페이지용 파라미터 전부 담기) ──
  const entryUrl = qrRoom
    ? [
        `${window.location.origin}/search?roomId=${qrRoom.id}`,
        `pw=${encodeURIComponent(qrRoom.password)}`,
        `day=${encodeURIComponent(qrRoom.day)}`,
        `department=${encodeURIComponent(qrRoom.department)}`,
        `year=${encodeURIComponent(qrRoom.year)}`,
        `period=${qrRoom.period}`,
        `subject=${encodeURIComponent(qrRoom.subject)}`
      ].join('&')
    : '';

  // 정렬 함수
  const sortList = (list, sortBy, type) => {
    const sorted = [...list];
    switch (sortBy) {
      case 'latest':
        return sorted.sort((a, b) => b.createdAt?.toDate() - a.createdAt?.toDate());
      case 'oldest':
        return sorted.sort((a, b) => a.createdAt?.toDate() - b.createdAt?.toDate());
      case 'subject':
        if (type === 'room') {
          return sorted.sort((a, b) => a.name.localeCompare(b.name));
        }
        return sorted;
      default:
        return sorted;
    }
  };

  const handleChangeIcon = idx => {
    setIconIdx(idx);
    onUpdateIconIdx(idx);
  };

  return (
    <CenterLayout>
      {/* 헤더 */}
      <div style={{
        marginBottom: '1.5rem',
        position: 'relative',
        background: 'white',
        padding: isMobile ? '1.2rem 1.5rem' : '1.5rem 2rem',
        borderRadius: '0.75rem',
        boxShadow: '0 2px 12px rgba(0, 0, 0, 0.04)',
        overflow: 'hidden',
        border: '1px solid #f4f4f8'
      }}>
        <div style={{
          position: 'absolute',
          left: '0',
          top: '0',
          width: '6px',
          height: '100%',
          background: '#4f46e5'
        }}></div>
        <h2 style={{
          margin: 0,
          marginLeft: '6px',
          fontSize: isMobile ? '1.3rem' : '1.5rem',
          fontWeight: 600,
          color: '#111827',
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: isMobile ? '36px' : '42px',
            height: isMobile ? '36px' : '42px',
            borderRadius: '12px',
            background: '#f3f4ff',
            color: '#4f46e5'
          }}>
            <svg xmlns="http://www.w3.org/2000/svg" width={isMobile ? "18" : "22"} height={isMobile ? "18" : "22"} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
              <circle cx="12" cy="7" r="4"></circle>
            </svg>
          </div>
          마이페이지
        </h2>
      </div>

      {/* 프로필 카드 */}
      <div style={{
        background: 'white',
        borderRadius: '0.75rem',
        boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
        padding: '1.5rem',
        marginBottom: '1.5rem',
        border: '1px solid #f1f5f9',
      }}>
        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'flex-start' : 'center',
          gap: '1.25rem',
          marginBottom: '0.5rem',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
          }}>
            <div style={{
              fontSize: '2.5rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '3.5rem',
              height: '3.5rem',
              background: 'rgba(99, 102, 241, 0.1)',
              borderRadius: '50%',
              flexShrink: 0,
              border: '2px solid rgba(99, 102, 241, 0.2)',
            }}>
              {user.isAnonymous ? '🕵️' : emojiList[0]}
            </div>
            <div>
              <h3 style={{
                margin: '0 0 0.5rem 0',
                fontSize: '1.25rem',
                fontWeight: 600,
                color: '#1e293b',
              }}>{user.nickname}</h3>
              <div style={{
                color: '#64748b',
                fontSize: '0.9rem',
              }}>
              </div>
            </div>
          </div>
          
          <div style={{
            display: 'flex',
            gap: '0.75rem',
            marginLeft: isMobile ? '0' : 'auto',
            marginTop: isMobile ? '0.75rem' : '0',
            flexWrap: 'wrap',
          }}>
            <button
              onClick={handleChangeNick}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.7rem 1rem',
                background: 'transparent',
                color: '#64748b',
                border: '1px solid #e2e8f0',
                borderRadius: '0.5rem',
                fontWeight: '500',
                fontSize: '0.95rem',
                cursor: 'pointer',
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/>
              </svg>
              닉네임 변경
            </button>
            <button
              onClick={onLogout}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.7rem 1rem',
                background: 'rgba(239, 68, 68, 0.05)',
                color: '#ef4444',
                border: '1px solid rgba(239, 68, 68, 0.1)',
                borderRadius: '0.5rem',
                fontWeight: '500',
                fontSize: '0.95rem',
                cursor: 'pointer',
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                <polyline points="16 17 21 12 16 7"></polyline>
                <line x1="21" y1="12" x2="9" y2="12"></line>
              </svg>
              로그아웃
            </button>
          </div>
        </div>
        
        <div style={{
          display: 'flex',
          gap: '0.75rem',
          marginTop: '1.25rem',
        }}>
          <div style={{
            flex: 1,
            padding: '0.75rem',
            background: 'rgba(99, 102, 241, 0.05)',
            borderRadius: '0.5rem',
            textAlign: 'center',
            border: '1px solid rgba(99, 102, 241, 0.1)',
          }}>
            <div style={{
              fontWeight: 700,
              fontSize: '1.25rem',
              color: '#6366f1',
              marginBottom: '0.25rem',
            }}>{rooms.length}</div>
            <div style={{
              fontSize: '0.85rem',
              color: '#64748b',
            }}>내 방</div>
          </div>
          <div style={{
            flex: 1,
            padding: '0.75rem',
            background: 'rgba(99, 102, 241, 0.05)',
            borderRadius: '0.5rem',
            textAlign: 'center',
            border: '1px solid rgba(99, 102, 241, 0.1)',
          }}>
            <div style={{
              fontWeight: 700,
              fontSize: '1.25rem',
              color: '#6366f1',
              marginBottom: '0.25rem',
            }}>{posts.length}</div>
            <div style={{
              fontSize: '0.85rem',
              color: '#64748b',
            }}>내 게시물</div>
          </div>
          <div style={{
            flex: 1,
            padding: '0.75rem',
            background: 'rgba(99, 102, 241, 0.05)',
            borderRadius: '0.5rem',
            textAlign: 'center',
            border: '1px solid rgba(99, 102, 241, 0.1)',
          }}>
            <div style={{
              fontWeight: 700,
              fontSize: '1.25rem',
              color: '#6366f1',
              marginBottom: '0.25rem',
            }}>{questions.length}</div>
            <div style={{
              fontSize: '0.85rem',
              color: '#64748b',
            }}>내 질문</div>
          </div>
        </div>
      </div>

      {/* 커스텀 스크롤바 스타일 */}
      <style jsx="true">{`
        .custom-scrollbar {
          scrollbar-width: thin;
          scroll-behavior: smooth;
          -webkit-overflow-scrolling: touch;
          padding-right: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(241, 245, 249, 0.8);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(99, 102, 241, 0.3);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(99, 102, 241, 0.5);
        }
        .content-wrapper {
          padding-bottom: 16px;
        }
        .swipe-container {
          transition: transform 0.3s ease-out;
        }
      `}</style>
      
      {/* 그리드 레이아웃을 모바일/데스크톱에 따라 다르게 표시 */}
      {isMobile ? (
        // 모바일 스와이프 UI
        <div style={{ width: '100%', marginBottom: '5rem' }}>
          {/* 탭 헤더 */}
          <div style={{
            display: 'flex',
            borderBottom: '1px solid #e2e8f0',
            marginBottom: '1rem',
            background: 'white',
            borderRadius: '0.75rem 0.75rem 0 0',
            overflow: 'hidden',
          }}>
            <button 
              onClick={() => setActiveTab(0)} 
              style={{
                flex: 1,
                padding: '0.8rem 0.5rem',
                border: 'none',
                background: activeTab === 0 ? '#f8fafc' : 'white',
                borderBottom: activeTab === 0 ? '2px solid #6366f1' : '2px solid transparent',
                color: activeTab === 0 ? '#4f46e5' : '#64748b',
                fontWeight: activeTab === 0 ? '600' : '500',
                fontSize: '0.9rem',
                cursor: 'pointer',
              }}
            >
              내가 만든 방 ({rooms.length})
            </button>
            <button 
              onClick={() => setActiveTab(1)} 
              style={{
                flex: 1,
                padding: '0.8rem 0.5rem',
                border: 'none',
                background: activeTab === 1 ? '#f8fafc' : 'white',
                borderBottom: activeTab === 1 ? '2px solid #6366f1' : '2px solid transparent',
                color: activeTab === 1 ? '#4f46e5' : '#64748b',
                fontWeight: activeTab === 1 ? '600' : '500',
                fontSize: '0.9rem',
                cursor: 'pointer',
              }}
            >
              내 게시물 ({posts.length})
            </button>
            <button 
              onClick={() => setActiveTab(2)} 
              style={{
                flex: 1,
                padding: '0.8rem 0.5rem',
                border: 'none',
                background: activeTab === 2 ? '#f8fafc' : 'white',
                borderBottom: activeTab === 2 ? '2px solid #6366f1' : '2px solid transparent',
                color: activeTab === 2 ? '#4f46e5' : '#64748b',
                fontWeight: activeTab === 2 ? '600' : '500',
                fontSize: '0.9rem',
                cursor: 'pointer',
              }}
            >
              내 질문 ({questions.length})
            </button>
          </div>
          
          {/* 스와이프 힌트 표시 */}
          <div style={{
            padding: '0.5rem',
            background: 'rgba(99, 102, 241, 0.08)',
            borderRadius: '0.5rem',
            margin: '0 0.5rem 1rem 0.5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            fontSize: '0.85rem',
            color: '#6366f1',
          }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 8l4 4-4 4"/>
              <path d="M3 12h8"/>
              <path d="M11 16l-4-4 4-4"/>
              <path d="M17 12h8"/>
            </svg>
            좌우로 스와이프하여 내용을 확인하세요
          </div>
          
          {/* 스와이프 가능한 컨텐츠 영역 */}
          <div {...swipeHandlers} style={{
            width: '100%',
            minHeight: '60vh',
            position: 'relative',
            overflow: 'hidden'
          }}>
            <div style={{
              display: 'flex',
              width: '300%',
              transform: `translateX(${activeTab * -33.333}%)`,
              transition: 'transform 0.3s ease-out',
            }} className="swipe-container">
              {/* 내가 만든 방 */}
              <div style={{
                width: '33.333%',
                padding: '0 0.5rem',
              }}>
                <div style={{
                  background: 'white',
                  borderRadius: '0.75rem',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                  padding: '1.2rem',
                  border: '1px solid #f1f5f9',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '1rem',
                  }}>
                    <h3 style={{
                      margin: 0,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      color: '#1e293b',
                    }}>내가 만든 방</h3>
                    <select 
                      value={roomSort} 
                      onChange={e => setRoomSort(e.target.value)}
                      style={{
                        padding: '0.5rem',
                        borderRadius: '0.5rem',
                        border: '1px solid #e2e8f0',
                        fontSize: '0.9rem',
                        appearance: 'none',
                        backgroundImage: `url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 0.5rem center',
                        backgroundSize: '1rem',
                        paddingRight: '2rem',
                        maxWidth: '120px',
                      }}
                    >
                      <option value="latest">최신순</option>
                      <option value="oldest">오래된순</option>
                      <option value="subject">과목순</option>
                    </select>
                  </div>
                  
                  <div style={{
                    flex: 1,
                    overflowY: 'auto',
                  }} className="custom-scrollbar">
                    {rooms.length > 0 ? (
                      <div className="content-wrapper">
                        {sortList(rooms, roomSort, 'room').map(r => (
                          <div 
                            key={r.id}
                            style={{
                              background: r.password ? 'linear-gradient(135deg, #fffbeb 0%, #fff7ed 100%)' : 'rgba(99, 102, 241, 0.05)',
                              borderRadius: '0.6rem',
                              padding: '1rem',
                              marginBottom: '0.75rem',
                              border: r.password ? '1px solid rgba(251, 191, 36, 0.3)' : '1px solid rgba(99, 102, 241, 0.15)',
                            }}
                          >
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              marginBottom: '0.75rem',
                            }}>
                              <div style={{
                                fontWeight: 600,
                                fontSize: '1rem',
                                color: '#1e293b',
                                marginBottom: '0.25rem',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                              }}>
                                {r.name}
                                {r.password && (
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#f59e42" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="3" y="11" width="18" height="8" rx="2" />
                                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                  </svg>
                                )}
                              </div>
                            </div>
                            
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              flexWrap: 'wrap',
                              marginBottom: '0.75rem',
                            }}>
                              <div style={{
                                fontSize: '0.85rem',
                                padding: '0.25rem 0.5rem',
                                borderRadius: '1rem',
                                background: 'rgba(99, 102, 241, 0.1)',
                                color: '#6366f1',
                                fontWeight: '500',
                              }}>
                                {r.day} {r.period}교시
                              </div>
                              <div style={{
                                fontSize: '0.85rem',
                                color: '#64748b',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.25rem',
                              }}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                                  <line x1="16" y1="2" x2="16" y2="6"/>
                                  <line x1="8" y1="2" x2="8" y2="6"/>
                                  <line x1="3" y1="10" x2="21" y2="10"/>
                                </svg>
                                {new Date(r.createdAt?.toDate()).toLocaleDateString()}
                              </div>
                            </div>
                            
                            <div style={{
                              display: 'flex',
                              gap: '0.5rem',
                              marginTop: '0.5rem',
                            }}>
                              <button 
                                onClick={() => navigate(`/rooms/${r.id}`)}
                                style={{
                                  flex: 1,
                                  padding: '0.6rem 0',
                                  background: '#6366f1',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '0.5rem',
                                  fontWeight: '600',
                                  fontSize: '0.9rem',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                  gap: '0.3rem',
                                }}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
                                  <polyline points="10 17 15 12 10 7"/>
                                  <line x1="15" y1="12" x2="3" y2="12"/>
                                </svg>
                                입장
                              </button>
                              <button 
                                onClick={() => openQrModal(r)}
                                style={{
                                  padding: '0.6rem',
                                  background: 'transparent',
                                  color: '#6366f1',
                                  border: '1px solid #6366f1',
                                  borderRadius: '0.5rem',
                                  fontWeight: '600',
                                  fontSize: '0.9rem',
                                  cursor: 'pointer',
                                }}
                              >
                                QR
                              </button>
                              <button 
                                onClick={() => handleDeleteRoom(r.id)}
                                style={{
                                  padding: '0.6rem',
                                  background: 'rgba(239, 68, 68, 0.1)',
                                  color: '#ef4444',
                                  border: '1px solid rgba(239, 68, 68, 0.2)',
                                  borderRadius: '0.5rem',
                                  fontWeight: '600',
                                  fontSize: '0.9rem',
                                  cursor: 'pointer',
                                }}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="3 6 5 6 21 6"/>
                                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                                  <line x1="10" y1="11" x2="10" y2="17"/>
                                  <line x1="14" y1="11" x2="14" y2="17"/>
                                </svg>
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '2rem 1rem',
                        color: '#94a3b8',
                        textAlign: 'center',
                        minHeight: '40vh',
                      }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M19 21V5a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v5m-4 0h4"/>
                        </svg>
                        <p style={{
                          margin: '1rem 0',
                          fontWeight: 500,
                          fontSize: '1rem',
                        }}>아직 만든 방이 없습니다.</p>
                        <button 
                          onClick={() => navigate('/create')}
                          style={{
                            padding: '0.75rem 1.25rem',
                            background: '#6366f1',
                            color: 'white',
                            border: 'none',
                            borderRadius: '0.5rem',
                            fontWeight: '600',
                            fontSize: '0.95rem',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                          }}
                        >
                          방 만들기
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* 작성한 게시물 */}
              <div style={{
                width: '33.333%',
                padding: '0 0.5rem',
              }}>
                {/* 게시물 컨텐츠 */}
                <div style={{
                  background: 'white',
                  borderRadius: '0.75rem',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                  padding: '1.2rem',
                  border: '1px solid #f1f5f9',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '1rem',
                  }}>
                    <h3 style={{
                      margin: 0,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      color: '#1e293b',
                    }}>작성한 게시물</h3>
                    <select 
                      value={postSort} 
                      onChange={e => setPostSort(e.target.value)}
                      style={{
                        padding: '0.5rem',
                        borderRadius: '0.5rem',
                        border: '1px solid #e2e8f0',
                        fontSize: '0.9rem',
                        appearance: 'none',
                        backgroundImage: `url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 0.5rem center',
                        backgroundSize: '1rem',
                        paddingRight: '2rem',
                        maxWidth: '120px',
                      }}
                    >
                      <option value="latest">최신순</option>
                      <option value="oldest">오래된순</option>
                    </select>
                  </div>
                  
                  <div style={{
                    flex: 1,
                    overflowY: 'auto',
                  }} className="custom-scrollbar">
                    {/* 기존 게시물 리스트 코드 */}
                    {posts.length > 0 ? (
                      <div className="content-wrapper">
                        {sortList(posts, postSort, 'post').map(p => (
                          <div 
                            key={p.id}
                            style={{
                              background: 'rgba(99, 102, 241, 0.05)',
                              borderRadius: '0.6rem',
                              padding: '1rem',
                              border: '1px solid rgba(99, 102, 241, 0.15)',
                              marginBottom: '0.75rem',
                            }}
                          >
                            <div style={{
                              fontWeight: 600,
                              fontSize: '1rem',
                              color: '#1e293b',
                              marginBottom: '0.5rem',
                            }}>
                              {p.title}
                            </div>
                            
                            <div style={{
                              fontSize: '0.9rem',
                              color: '#475569',
                              marginBottom: '0.75rem',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              lineHeight: 1.5,
                            }}>
                              {p.content.slice(0, 100) + (p.content.length > 100 ? '...' : '')}
                            </div>
                            
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              marginBottom: '0.75rem',
                            }}>
                              <div style={{
                                fontSize: '0.85rem',
                                color: '#64748b',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.25rem',
                              }}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                                  <line x1="16" y1="2" x2="16" y2="6"/>
                                  <line x1="8" y1="2" x2="8" y2="6"/>
                                  <line x1="3" y1="10" x2="21" y2="10"/>
                                </svg>
                                {new Date(p.createdAt?.toDate()).toLocaleDateString()}
                              </div>
                              {p.isAnonymous && (
                                <div style={{
                                  fontSize: '0.85rem',
                                  padding: '0.25rem 0.5rem',
                                  borderRadius: '1rem',
                                  background: 'rgba(99, 102, 241, 0.1)',
                                  color: '#6366f1',
                                  fontWeight: '500',
                                }}>
                                  익명
                                </div>
                              )}
                            </div>
                            
                            <div style={{
                              display: 'flex',
                              gap: '0.5rem',
                              marginTop: '0.5rem',
                            }}>
                              <button 
                                onClick={() => navigate(`/board/${p.id}`)}
                                style={{
                                  flex: 1,
                                  padding: '0.6rem 0',
                                  background: '#6366f1',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '0.5rem',
                                  fontWeight: '600',
                                  fontSize: '0.9rem',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                  gap: '0.3rem',
                                }}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                                  <circle cx="12" cy="12" r="3"/>
                                </svg>
                                보기
                              </button>
                              <button 
                                onClick={() => handleDeletePost(p.id)}
                                style={{
                                  padding: '0.6rem',
                                  background: 'rgba(239, 68, 68, 0.1)',
                                  color: '#ef4444',
                                  border: '1px solid rgba(239, 68, 68, 0.2)',
                                  borderRadius: '0.5rem',
                                  fontWeight: '600',
                                  fontSize: '0.9rem',
                                  cursor: 'pointer',
                                }}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="3 6 5 6 21 6"/>
                                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                                  <line x1="10" y1="11" x2="10" y2="17"/>
                                  <line x1="14" y1="11" x2="14" y2="17"/>
                                </svg>
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '2rem 1rem',
                        color: '#94a3b8',
                        textAlign: 'center',
                        minHeight: '40vh',
                      }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M19 20H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v1m2 13a2 2 0 0 1-2-2V7m2 13a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"/>
                        </svg>
                        <p style={{
                          margin: '1rem 0',
                          fontWeight: 500,
                          fontSize: '1rem',
                        }}>아직 작성한 게시물이 없습니다.</p>
                        <button 
                          onClick={() => navigate('/board/create')}
                          style={{
                            padding: '0.75rem 1.25rem',
                            background: '#6366f1',
                            color: 'white',
                            border: 'none',
                            borderRadius: '0.5rem',
                            fontWeight: '600',
                            fontSize: '0.95rem',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                          }}
                        >
                          게시물 작성
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* 작성한 질문 */}
              <div style={{
                width: '33.333%',
                padding: '0 0.5rem',
              }}>
                {/* 질문 컨텐츠 */}
                <div style={{
                  background: 'white',
                  borderRadius: '0.75rem',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                  padding: '1.2rem',
                  border: '1px solid #f1f5f9',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '1rem',
                  }}>
                    <h3 style={{
                      margin: 0,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      color: '#1e293b',
                    }}>작성한 질문</h3>
                    <select 
                      value={questionSort} 
                      onChange={e => setQuestionSort(e.target.value)}
                      style={{
                        padding: '0.5rem',
                        borderRadius: '0.5rem',
                        border: '1px solid #e2e8f0',
                        fontSize: '0.9rem',
                        appearance: 'none',
                        backgroundImage: `url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 0.5rem center',
                        backgroundSize: '1rem',
                        paddingRight: '2rem',
                        maxWidth: '120px',
                      }}
                    >
                      <option value="latest">최신순</option>
                      <option value="oldest">오래된순</option>
                    </select>
                  </div>
                  
                  <div style={{
                    flex: 1,
                    overflowY: 'auto',
                  }} className="custom-scrollbar">
                    {/* 기존 질문 리스트 코드 */}
                    {questions.length > 0 ? (
                      <div className="content-wrapper">
                        {sortList(questions, questionSort, 'question').map(q => (
                          <div 
                            key={q.id}
                            style={{
                              background: 'rgba(99, 102, 241, 0.05)',
                              borderRadius: '0.6rem',
                              padding: '1rem',
                              border: '1px solid rgba(99, 102, 241, 0.15)',
                              marginBottom: '0.75rem',
                            }}
                          >
                            <div style={{
                              fontWeight: 600,
                              fontSize: '1rem',
                              color: '#1e293b',
                              marginBottom: '0.5rem',
                            }}>
                              {q.text}
                            </div>
                            
                            <div style={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              alignItems: 'center',
                              gap: '0.5rem',
                              marginBottom: '0.75rem',
                            }}>
                              <div style={{
                                fontSize: '0.85rem',
                                padding: '0.25rem 0.5rem',
                                borderRadius: '1rem',
                                background: 'rgba(251, 191, 36, 0.1)',
                                color: '#d97706',
                                fontWeight: '500',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.25rem',
                              }}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M19 21V5a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v5m-4 0h4"/>
                                </svg>
                                {q.roomName}
                              </div>
                              <div style={{
                                fontSize: '0.85rem',
                                color: '#64748b',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.25rem',
                              }}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                                  <line x1="16" y1="2" x2="16" y2="6"/>
                                  <line x1="8" y1="2" x2="8" y2="6"/>
                                  <line x1="3" y1="10" x2="21" y2="10"/>
                                </svg>
                                {new Date(q.createdAt?.toDate()).toLocaleDateString()}
                              </div>
                            </div>
                            
                            <div style={{
                              display: 'flex',
                              gap: '0.5rem',
                              marginTop: '0.5rem',
                            }}>
                              <button 
                                onClick={() => navigate(`/rooms/${q.roomId}`)}
                                style={{
                                  flex: 1,
                                  padding: '0.6rem 0',
                                  background: '#6366f1',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '0.5rem',
                                  fontWeight: '600',
                                  fontSize: '0.9rem',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                  gap: '0.3rem',
                                }}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
                                  <polyline points="10 17 15 12 10 7"/>
                                  <line x1="15" y1="12" x2="3" y2="12"/>
                                </svg>
                                방으로
                              </button>
                              <button 
                                onClick={() => handleDeleteQuestion(q.roomId, q.id)}
                                style={{
                                  padding: '0.6rem',
                                  background: 'rgba(239, 68, 68, 0.1)',
                                  color: '#ef4444',
                                  border: '1px solid rgba(239, 68, 68, 0.2)',
                                  borderRadius: '0.5rem',
                                  fontWeight: '600',
                                  fontSize: '0.9rem',
                                  cursor: 'pointer',
                                }}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="3 6 5 6 21 6"/>
                                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                                  <line x1="10" y1="11" x2="10" y2="17"/>
                                  <line x1="14" y1="11" x2="14" y2="17"/>
                                </svg>
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '2rem 1rem',
                        color: '#94a3b8',
                        textAlign: 'center',
                        minHeight: '40vh',
                      }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10"/>
                          <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
                          <line x1="12" y1="17" x2="12.01" y2="17"/>
                        </svg>
                        <p style={{
                          margin: '1rem 0',
                          fontWeight: 500,
                          fontSize: '1rem',
                        }}>아직 작성한 질문이 없습니다.</p>
                        <button 
                          onClick={() => navigate('/search')}
                          style={{
                            padding: '0.75rem 1.25rem',
                            background: '#6366f1',
                            color: 'white',
                            border: 'none',
                            borderRadius: '0.5rem',
                            fontWeight: '600',
                            fontSize: '0.95rem',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                          }}
                        >
                          방 찾아보기
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        // 데스크톱 그리드 레이아웃
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '1.25rem',
          marginBottom: '2rem',
          width: '100%',
          padding: 0,
          height: 'calc(100vh - 320px)',
        }}>
          {/* 내가 만든 방 */}
          <div style={{
            background: 'white',
            borderRadius: '0.75rem',
            boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
            padding: '1.5rem',
            border: '1px solid #f1f5f9',
            display: 'flex',
            flexDirection: 'column',
            minHeight: '200px',
            height: '100%',
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1rem',
            }}>
              <h3 style={{
                margin: 0,
                fontSize: '1.1rem',
                fontWeight: 600,
                color: '#1e293b',
              }}>내가 만든 방</h3>
              <select 
                value={roomSort} 
                onChange={e => setRoomSort(e.target.value)}
                style={{
                  padding: '0.5rem',
                  borderRadius: '0.5rem',
                  border: '1px solid #e2e8f0',
                  fontSize: '0.9rem',
                  appearance: 'none',
                  backgroundImage: `url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 0.5rem center',
                  backgroundSize: '1rem',
                  paddingRight: '2rem',
                  maxWidth: '120px',
                }}
              >
                <option value="latest">최신순</option>
                <option value="oldest">오래된순</option>
                <option value="subject">과목순</option>
              </select>
            </div>

            {rooms.length > 0 ? (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.75rem',
                overflow: 'auto',
                flex: 1,
                maxHeight: 'calc(100vh - 430px)',
              }} className="custom-scrollbar">
                <div className="content-wrapper">
                  {sortList(rooms, roomSort, 'room').map(r => (
                    <div 
                      key={r.id}
                      style={{
                        background: r.password ? 'linear-gradient(135deg, #fffbeb 0%, #fff7ed 100%)' : 'rgba(99, 102, 241, 0.05)',
                        borderRadius: '0.6rem',
                        padding: '1rem',
                        border: r.password ? '1px solid rgba(251, 191, 36, 0.3)' : '1px solid rgba(99, 102, 241, 0.15)',
                      }}
                    >
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginBottom: '0.75rem',
                      }}>
                        <div style={{
                          fontWeight: 600,
                          fontSize: '1rem',
                          color: '#1e293b',
                          marginBottom: '0.25rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                        }}>
                          {r.name}
                          {r.password && (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#f59e42" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="3" y="11" width="18" height="8" rx="2" />
                              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                            </svg>
                          )}
                        </div>
                      </div>
                      
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        flexWrap: 'wrap',
                        marginBottom: '0.75rem',
                      }}>
                        <div style={{
                          fontSize: '0.85rem',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '1rem',
                          background: 'rgba(99, 102, 241, 0.1)',
                          color: '#6366f1',
                          fontWeight: '500',
                        }}>
                          {r.day} {r.period}교시
                        </div>
                        <div style={{
                          fontSize: '0.85rem',
                          color: '#64748b',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                        }}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                            <line x1="16" y1="2" x2="16" y2="6"/>
                            <line x1="8" y1="2" x2="8" y2="6"/>
                            <line x1="3" y1="10" x2="21" y2="10"/>
                          </svg>
                          {new Date(r.createdAt?.toDate()).toLocaleDateString()}
                        </div>
                      </div>
                      
                      <div style={{
                        display: 'flex',
                        gap: '0.5rem',
                        marginTop: '0.5rem',
                      }}>
                        <button 
                          onClick={() => navigate(`/rooms/${r.id}`)}
                          style={{
                            flex: 1,
                            padding: '0.6rem 0',
                            background: '#6366f1',
                            color: 'white',
                            border: 'none',
                            borderRadius: '0.5rem',
                            fontWeight: '600',
                            fontSize: '0.9rem',
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: '0.3rem',
                          }}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
                            <polyline points="10 17 15 12 10 7"/>
                            <line x1="15" y1="12" x2="3" y2="12"/>
                          </svg>
                          입장
                        </button>
                        <button 
                          onClick={() => openQrModal(r)}
                          style={{
                            padding: '0.6rem',
                            background: 'transparent',
                            color: '#6366f1',
                            border: '1px solid #6366f1',
                            borderRadius: '0.5rem',
                            fontWeight: '600',
                            fontSize: '0.9rem',
                            cursor: 'pointer',
                          }}
                        >
                          QR
                        </button>
                        <button 
                          onClick={() => handleDeleteRoom(r.id)}
                          style={{
                            padding: '0.6rem',
                            background: 'rgba(239, 68, 68, 0.1)',
                            color: '#ef4444',
                            border: '1px solid rgba(239, 68, 68, 0.2)',
                            borderRadius: '0.5rem',
                            fontWeight: '600',
                            fontSize: '0.9rem',
                            cursor: 'pointer',
                          }}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6"/>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                            <line x1="10" y1="11" x2="10" y2="17"/>
                            <line x1="14" y1="11" x2="14" y2="17"/>
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '2rem 1rem',
                color: '#94a3b8',
                textAlign: 'center',
              }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M19 21V5a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v5m-4 0h4"/>
                </svg>
                <p style={{
                  margin: '1rem 0',
                  fontWeight: 500,
                  fontSize: '1rem',
                }}>아직 만든 방이 없습니다.</p>
                <button 
                  onClick={() => navigate('/create')}
                  style={{
                    padding: '0.75rem 1.25rem',
                    background: '#6366f1',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.5rem',
                    fontWeight: '600',
                    fontSize: '0.95rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="5" x2="12" y2="19"></line>
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                  </svg>
                  방 만들기
                </button>
              </div>
            )}
          </div>

          {/* 작성한 게시물 */}
          <div style={{
            background: 'white',
            borderRadius: '0.75rem',
            boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
            padding: '1.5rem',
            border: '1px solid #f1f5f9',
            display: 'flex',
            flexDirection: 'column',
            minHeight: '200px',
            height: '100%',
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1rem',
            }}>
              <h3 style={{
                margin: 0,
                fontSize: '1.1rem',
                fontWeight: 600,
                color: '#1e293b',
              }}>작성한 게시물</h3>
              <select 
                value={postSort} 
                onChange={e => setPostSort(e.target.value)}
                style={{
                  padding: '0.5rem',
                  borderRadius: '0.5rem',
                  border: '1px solid #e2e8f0',
                  fontSize: '0.9rem',
                  appearance: 'none',
                  backgroundImage: `url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 0.5rem center',
                  backgroundSize: '1rem',
                  paddingRight: '2rem',
                  maxWidth: '120px',
                }}
              >
                <option value="latest">최신순</option>
                <option value="oldest">오래된순</option>
              </select>
            </div>

            {posts.length > 0 ? (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.75rem',
                overflow: 'auto',
                flex: 1,
                maxHeight: 'calc(100vh - 430px)',
              }} className="custom-scrollbar">
                <div className="content-wrapper">
                  {sortList(posts, postSort, 'post').map(p => (
                    <div 
                      key={p.id}
                      style={{
                        background: 'rgba(99, 102, 241, 0.05)',
                        borderRadius: '0.6rem',
                        padding: '1rem',
                        border: '1px solid rgba(99, 102, 241, 0.15)',
                      }}
                    >
                      <div style={{
                        fontWeight: 600,
                        fontSize: '1rem',
                        color: '#1e293b',
                        marginBottom: '0.5rem',
                      }}>
                        {p.title}
                      </div>
                      
                      <div style={{
                        fontSize: '0.9rem',
                        color: '#475569',
                        marginBottom: '0.75rem',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        lineHeight: 1.5,
                      }}>
                        {p.content.slice(0, 100) + (p.content.length > 100 ? '...' : '')}
                      </div>
                      
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        marginBottom: '0.75rem',
                      }}>
                        <div style={{
                          fontSize: '0.85rem',
                          color: '#64748b',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                        }}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                            <line x1="16" y1="2" x2="16" y2="6"/>
                            <line x1="8" y1="2" x2="8" y2="6"/>
                            <line x1="3" y1="10" x2="21" y2="10"/>
                          </svg>
                          {new Date(p.createdAt?.toDate()).toLocaleDateString()}
                        </div>
                        {p.isAnonymous && (
                          <div style={{
                            fontSize: '0.85rem',
                            padding: '0.25rem 0.5rem',
                            borderRadius: '1rem',
                            background: 'rgba(99, 102, 241, 0.1)',
                            color: '#6366f1',
                            fontWeight: '500',
                          }}>
                            익명
                          </div>
                        )}
                      </div>
                      
                      <div style={{
                        display: 'flex',
                        gap: '0.5rem',
                        marginTop: '0.5rem',
                      }}>
                        <button 
                          onClick={() => navigate(`/board/${p.id}`)}
                          style={{
                            flex: 1,
                            padding: '0.6rem 0',
                            background: '#6366f1',
                            color: 'white',
                            border: 'none',
                            borderRadius: '0.5rem',
                            fontWeight: '600',
                            fontSize: '0.9rem',
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: '0.3rem',
                          }}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                            <circle cx="12" cy="12" r="3"/>
                          </svg>
                          보기
                        </button>
                        <button 
                          onClick={() => handleDeletePost(p.id)}
                          style={{
                            padding: '0.6rem',
                            background: 'rgba(239, 68, 68, 0.1)',
                            color: '#ef4444',
                            border: '1px solid rgba(239, 68, 68, 0.2)',
                            borderRadius: '0.5rem',
                            fontWeight: '600',
                            fontSize: '0.9rem',
                            cursor: 'pointer',
                          }}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6"/>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                            <line x1="10" y1="11" x2="10" y2="17"/>
                            <line x1="14" y1="11" x2="14" y2="17"/>
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '2rem 1rem',
                color: '#94a3b8',
                textAlign: 'center',
              }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M19 20H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v1m2 13a2 2 0 0 1-2-2V7m2 13a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"/>
                </svg>
                <p style={{
                  margin: '1rem 0',
                  fontWeight: 500,
                  fontSize: '1rem',
                }}>아직 작성한 게시물이 없습니다.</p>
                <button 
                  onClick={() => navigate('/board/create')}
                  style={{
                    padding: '0.75rem 1.25rem',
                    background: '#6366f1',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.5rem',
                    fontWeight: '600',
                    fontSize: '0.95rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="5" x2="12" y2="19"></line>
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                  </svg>
                  게시물 작성
                </button>
              </div>
            )}
          </div>

          {/* 작성한 질문 */}
          <div style={{
            background: 'white',
            borderRadius: '0.75rem',
            boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
            padding: '1.5rem',
            border: '1px solid #f1f5f9',
            display: 'flex',
            flexDirection: 'column',
            minHeight: '200px',
            height: '100%',
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1rem',
            }}>
              <h3 style={{
                margin: 0,
                fontSize: '1.1rem',
                fontWeight: 600,
                color: '#1e293b',
              }}>작성한 질문</h3>
              <select 
                value={questionSort} 
                onChange={e => setQuestionSort(e.target.value)}
                style={{
                  padding: '0.5rem',
                  borderRadius: '0.5rem',
                  border: '1px solid #e2e8f0',
                  fontSize: '0.9rem',
                  appearance: 'none',
                  backgroundImage: `url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 0.5rem center',
                  backgroundSize: '1rem',
                  paddingRight: '2rem',
                  maxWidth: '120px',
                }}
              >
                <option value="latest">최신순</option>
                <option value="oldest">오래된순</option>
              </select>
            </div>

            {questions.length > 0 ? (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.75rem',
                overflow: 'auto',
                flex: 1,
                maxHeight: 'calc(100vh - 430px)',
              }} className="custom-scrollbar">
                <div className="content-wrapper">
                  {sortList(questions, questionSort, 'question').map(q => (
                    <div 
                      key={q.id}
                      style={{
                        background: 'rgba(99, 102, 241, 0.05)',
                        borderRadius: '0.6rem',
                        padding: '1rem',
                        border: '1px solid rgba(99, 102, 241, 0.15)',
                      }}
                    >
                      <div style={{
                        fontWeight: 600,
                        fontSize: '1rem',
                        color: '#1e293b',
                        marginBottom: '0.5rem',
                      }}>
                        {q.text}
                      </div>
                      
                      <div style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: '0.5rem',
                        marginBottom: '0.75rem',
                      }}>
                        <div style={{
                          fontSize: '0.85rem',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '1rem',
                          background: 'rgba(251, 191, 36, 0.1)',
                          color: '#d97706',
                          fontWeight: '500',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                        }}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M19 21V5a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v5m-4 0h4"/>
                          </svg>
                          {q.roomName}
                        </div>
                        <div style={{
                          fontSize: '0.85rem',
                          color: '#64748b',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                        }}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                            <line x1="16" y1="2" x2="16" y2="6"/>
                            <line x1="8" y1="2" x2="8" y2="6"/>
                            <line x1="3" y1="10" x2="21" y2="10"/>
                          </svg>
                          {new Date(q.createdAt?.toDate()).toLocaleDateString()}
                        </div>
                      </div>
                      
                      <div style={{
                        display: 'flex',
                        gap: '0.5rem',
                        marginTop: '0.5rem',
                      }}>
                        <button 
                          onClick={() => navigate(`/rooms/${q.roomId}`)}
                          style={{
                            flex: 1,
                            padding: '0.6rem 0',
                            background: '#6366f1',
                            color: 'white',
                            border: 'none',
                            borderRadius: '0.5rem',
                            fontWeight: '600',
                            fontSize: '0.9rem',
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: '0.3rem',
                          }}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
                            <polyline points="10 17 15 12 10 7"/>
                            <line x1="15" y1="12" x2="3" y2="12"/>
                          </svg>
                          방으로
                        </button>
                        <button 
                          onClick={() => handleDeleteQuestion(q.roomId, q.id)}
                          style={{
                            padding: '0.6rem',
                            background: 'rgba(239, 68, 68, 0.1)',
                            color: '#ef4444',
                            border: '1px solid rgba(239, 68, 68, 0.2)',
                            borderRadius: '0.5rem',
                            fontWeight: '600',
                            fontSize: '0.9rem',
                            cursor: 'pointer',
                          }}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6"/>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                            <line x1="10" y1="11" x2="10" y2="17"/>
                            <line x1="14" y1="11" x2="14" y2="17"/>
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '2rem 1rem',
                color: '#94a3b8',
                textAlign: 'center',
              }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
                  <line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
                <p style={{
                  margin: '1rem 0',
                  fontWeight: 500,
                  fontSize: '1rem',
                }}>아직 작성한 질문이 없습니다.</p>
                <button 
                  onClick={() => navigate('/search')}
                  style={{
                    padding: '0.75rem 1.25rem',
                    background: '#6366f1',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.5rem',
                    fontWeight: '600',
                    fontSize: '0.95rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8"/>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                  방 찾아보기
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* QR 모달 */}
      {showQr && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: isMobile ? '1rem' : '2rem',
          }}
          onClick={closeQrModal}
        >
          <div 
            style={{
              background: 'white',
              borderRadius: '0.75rem',
              padding: '2rem',
              width: isMobile ? '90%' : '400px',
              maxWidth: '500px',
              boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
              textAlign: 'center',
              position: 'relative',
            }}
            onClick={e => e.stopPropagation()}
          >
            <button
              style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                background: 'transparent',
                border: 'none',
                fontSize: '1.25rem',
                cursor: 'pointer',
                color: '#64748b',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '2rem',
                height: '2rem',
                borderRadius: '50%',
              }}
              onClick={closeQrModal}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>

            <h3 style={{
              margin: '0 0 1.5rem 0',
              fontSize: '1.25rem',
              fontWeight: 600,
              color: '#1e293b',
            }}>
              방 입장 QR코드
            </h3>
            
            <div style={{
              background: 'white',
              padding: '1rem',
              display: 'inline-block',
              borderRadius: '0.5rem',
              border: '1px solid #e2e8f0',
              marginBottom: '1.5rem',
            }}>
              <QRCodeCanvas value={entryUrl} size={200} />
            </div>
            
            <div style={{
              fontSize: '0.95rem',
              color: '#64748b',
              marginBottom: '1.5rem',
              lineHeight: 1.5,
            }}>
              위 QR코드를 스캔하여<br />
              참가자들이 방에 바로 입장할 수 있습니다.
            </div>
            
            <div style={{
              background: 'rgba(99, 102, 241, 0.05)',
              padding: '0.75rem',
              borderRadius: '0.5rem',
              fontSize: '0.9rem',
              marginBottom: '1.5rem',
              color: '#64748b',
              border: '1px solid rgba(99, 102, 241, 0.1)',
            }}>
              <div style={{
                fontWeight: 600,
                marginBottom: '0.25rem',
                color: '#6366f1',
              }}>방 정보</div>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.25rem',
                textAlign: 'left',
              }}>
                <div>과목: {qrRoom?.subject}</div>
                <div>요일: {qrRoom?.day} {qrRoom?.period}교시</div>
                {qrRoom?.password && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                    color: '#d97706',
                  }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="11" width="18" height="8" rx="2" />
                      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                    비밀번호 설정됨
                  </div>
                )}
              </div>
            </div>
            
            <button 
              style={{
                width: '100%',
                padding: '0.75rem',
                background: '#6366f1',
                color: 'white',
                border: 'none',
                borderRadius: '0.5rem',
                fontWeight: '600',
                fontSize: '0.95rem',
                cursor: 'pointer',
              }}
              onClick={closeQrModal}
            >
              닫기
            </button>
          </div>
        </div>
      )}
    </CenterLayout>
  );
}
